import {
    createContext,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
} from "react";
import { Navigate, Outlet, useOutletContext } from "react-router-dom";

import useFCSubscriptionStore from "@hsl/fc-subscription/store";

import { type Config } from "../../config/types";
import { useBannerImage } from "../../hooks/useBannerImage";

const BannerTextContext = createContext<{
    title?: string;
    text?: string;
}>({});

type AudienceMeta = {
    userTitles: string[];
};

interface Props {
    banner: {
        banner?: string;
        title?: string;
        text?: string;
    };
    config: Config;
    audienceMeta: AudienceMeta;
    enabled?: boolean;
}

type SubscriptionContext = { config: Config; audienceMeta: AudienceMeta };

export const SubscriptionRoot = ({
    banner: { banner, title, text },
    config,
    audienceMeta,
    enabled,
}: Props) => {
    useBannerImage({ banner });
    const latestFC = useFCSubscriptionStore((state) => state.latestFC);

    useEffect(() => {
        const importantInfo = document.querySelectorAll(
            "#important-information > div",
        );
        console.log("IMPORRTANT INFO FCS");
        importantInfo.forEach((info) => {
            if (info.classList.contains("fcs")) {
                info.classList.remove("hidden");
            } else {
                info.classList.add("hidden");
            }
        });
        return () => {
            const importantInfo = document.querySelectorAll(
                "#important-information > div",
            );
            importantInfo.forEach((info) => {
                info.classList.add("hidden");
            });
        };
    }, []);

    const value = useMemo(
        () => ({
            title: title,
            text: text,
        }),
        [title, text],
    );

    if (!enabled) return <Navigate to={latestFC} />;

    return (
        <BannerTextContext.Provider value={value}>
            <div className="bg-white-smoke min-h-[50vh] w-full pb-8">
                <div className="container mx-auto">
                    <Outlet
                        context={
                            {
                                config,
                                audienceMeta,
                            } satisfies SubscriptionContext
                        }
                    />
                </div>
            </div>
        </BannerTextContext.Provider>
    );
};

export const useBannerText = () => {
    const context = useContext(BannerTextContext);

    useLayoutEffect(() => {
        const title = document.querySelector("h1.banner-header");
        if (!title) {
            console.warn("No banner title found");
            return;
        }
        title.classList.add("hidden");

        const overrideBannerContent = document.createElement("div");
        overrideBannerContent.innerHTML = `
            <h1 class="banner-header">
                ${context.title}
            </h1>
            ${context.text}
        `;
        overrideBannerContent.querySelectorAll("p").forEach((p) => {
            p.classList.add("mt-1", "hidden", "sm:block");
        });

        title.after(overrideBannerContent);

        return () => {
            title.classList.remove("hidden");
            overrideBannerContent.remove();
        };
    }, [context]);
};

export function useSubscriptionContext() {
    return useOutletContext<SubscriptionContext>();
}
